/**
 * Deeply compares newData and oldData and returns only the changed fields.
 * Can optionally ignore array order for specified keys.
 *
 * @param {object} newData - The new form values
 * @param {object} oldData - The original data from the backend
 * @param {object} [options]
 * @param {string[]} [options.ignoreOrderKeys] - Keys whose arrays should be compared without regard to order
 * @returns {object} - Only the changed fields
 */
export function getChangedFieldsDeep(newData, oldData, options = {}) {
    const changed = {};
    const ignoreOrderKeys = options.ignoreOrderKeys || [];

    for (const key in newData) {
        // If key is in ignoreOrderKeys, always include it regardless of value or type
        if (ignoreOrderKeys.includes(key)) {
            changed[key] = newData[key];
            continue; // Skip the normal comparison logic
        }

        const newVal = normalizeValue(newData[key]);
        const oldVal = normalizeValue(oldData[key]);

        // Normal comparison logic for keys not in ignoreOrderKeys
        if (!deepEqual(newVal, oldVal)) {
            changed[key] = newData[key]; // preserve original value (not normalized)
        }
    }

    return changed;
}

function normalizeValue(value) {
    if (value instanceof Date) return value.getTime();

    return value === undefined ? null : value;
}

function deepEqual(a, b) {
    if (a === b) return true;

    if (typeof a !== typeof b || a === null || b === null) return false;

    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) return false;
        return a.every((item, i) => deepEqual(item, b[i]));
    }

    if (typeof a === 'object') {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;

        return aKeys.every((key) => deepEqual(a[key], b[key]));
    }

    return false;
}
